import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Shell } from "@/components/Shell";
import { usePlanning } from "@/features/planning/usePlanning";
import { SignIn } from "@/features/session/SignIn";
import { useMe } from "@/features/session/useMe";
import { useSeizoenen } from "@/features/session/useSeizoenen";
import { bestemmingen, type Me } from "@/features/session/toegang";

/**
 * Unlike the Backoffice's `BeheerderShell`, this site refuses nobody who signed in: a session
 * decides which screens it is offered, not whether it is let in. The children are a function so
 * every screen gets `me` without repeating the doors above it.
 */
export function AanmeldShell({
  titel,
  uitleg,
  voordeur,
  terug,
  wide,
  children,
}: {
  titel: string;
  uitleg: ReactNode;
  /** True on the front door itself, which has nowhere above it to go back to. */
  voordeur?: boolean;
  /**
   * Given rather than derived, because only the screen knows what it was opened from. Wins over
   * the menu link: the way out of a detail is the list it was on.
   */
  terug?: { to: string; label: string };
  wide?: boolean;
  children: (me: Me) => ReactNode;
}) {
  const { t } = useTranslation(["common", "overzicht"]);
  const { data: me, isPending } = useMe();

  // Whether the front door is a screen this session stays on, which is what decides whether
  // there is anything to go back *to*. The same season query the planning blocks are drawn
  // from, so on the way in from the front door it is already answered and this costs nothing.
  const { data: seizoenen } = useSeizoenen();
  const { data: planning } = usePlanning(seizoenen?.[0]?.id);

  if (isPending) {
    return (
      <Shell>
        <PageSkeleton />
      </Shell>
    );
  }

  if (!me) {
    return (
      <Shell>
        <SignIn />
      </Shell>
    );
  }

  const uitweg = bepaalUitweg(me, Boolean(planning), voordeur, terug, t("overzicht:terug"));

  return (
    <Shell name={me.name} wide={wide}>
      <header className="mb-6 space-y-1">
        {uitweg && (
          <Link
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            to={uitweg.to}
          >
            <ChevronLeft className="size-4" />
            {uitweg.label}
          </Link>
        )}
        <h1 className="text-2xl font-bold tracking-[-0.02em]">{titel}</h1>
        <p className="text-sm text-muted-foreground">{uitleg}</p>
      </header>
      {children(me)}
    </Shell>
  );
}

/**
 * Only where there is a front door to return to, and that is `bestemmingen`' question rather
 * than this component's: a session the front door immediately sends back here has no way out
 * to offer, and a link that lands and bounces is worse than none. Everybody else does get one
 * - there is no menu on this site, so this link is the only way back up out of a block.
 *
 * A screen that named its own way back keeps it either way, since that link leads to the list
 * it was opened from rather than to the front door two levels up.
 */
function bepaalUitweg(
  me: Me,
  heeftPlanning: boolean,
  voordeur: boolean | undefined,
  terug: { to: string; label: string } | undefined,
  terugNaarOverzicht: string,
): { to: string; label: string } | null {
  return (
    terug ??
    (!voordeur && bestemmingen(me, heeftPlanning).doorsturen === null
      ? { to: "/", label: terugNaarOverzicht }
      : null)
  );
}
